import {
  BannerTab,
  CardTab,
  Content,
  FormattedBannerTab,
  FormattedCardTab,
  FormattedProps
} from './types';
import { EngagementUnit } from '../shared/types';
import { getLinkHref } from 'shared/helpers/uri';
import { Link, PropsFormatterUtils } from 'shared/types';
import { BREAKPOINTS } from 'shared/components/presentational/Picture/constants';

export default (
  content: Content,
  { getLinkHrefWithAuthentication }: PropsFormatterUtils
): FormattedProps => {
  const formatBanner = (tab: BannerTab): FormattedBannerTab => ({
    template: tab.template,
    tabTitle: tab.tab_title,
    autoPlaySpeed: tab.autoPlaySpeed ? Number(tab.autoPlaySpeed) : null,
    contents: tab.products.map(product => ({
      title: product.title,
      description: product.description,
      primaryButton:
        product.primary_button?.label && product.primary_button?.link
          ? {
              label: product.primary_button.label,
              link: getLinkHrefWithAuthentication(
                product.primary_button.link as Link
              ),
              target:
                product.primary_button.link.rel === 'external'
                  ? '_blank'
                  : '_self'
            }
          : null,
      bannerUrlByDevice: {
        desktop: {
          url: getLinkHref(product.media.desktop_banner.link as Link),
          alt: product.media.desktop_banner.description || '',
          w: BREAKPOINTS.DESKTOP + 1,
          isVideo: false
        },
        tablet: {
          url: getLinkHref(
            (product.media?.tablet_banner?.link as Link | undefined) ||
              (product.media.desktop_banner.link as Link)
          ),
          alt:
            product.media?.tablet_banner?.description ||
            product.media?.desktop_banner?.description ||
            '',
          w: BREAKPOINTS.DESKTOP,
          isVideo: false
        },
        mobile: {
          url: getLinkHref(
            (product.media?.mobile_banner?.link as Link | undefined) ||
              (product.media?.tablet_banner?.link as Link | undefined) ||
              (product.media.desktop_banner.link as Link)
          ),
          alt:
            product.media?.mobile_banner?.description ||
            product.media?.tablet_banner?.description ||
            product.media?.desktop_banner?.description ||
            '',
          w: BREAKPOINTS.PHABLET,
          isVideo: false
        },
        mobileMini: {
          url: getLinkHref(
            (product.media?.mobile_banner?.link as Link | undefined) ||
              (product.media?.tablet_banner?.link as Link | undefined) ||
              (product.media.desktop_banner.link as Link)
          ),
          alt:
            product.media?.mobile_banner?.description ||
            product.media?.tablet_banner?.description ||
            product.media?.desktop_banner?.description ||
            '',
          w: BREAKPOINTS.PHONE,
          isVideo: false
        }
      }
    }))
  });

  const formatCard = (tab: CardTab): FormattedCardTab => ({
    template: tab.template,
    tabTitle: tab.tab_title,
    title: tab.title ?? undefined,
    subtitle: tab.subtitle,
    contents: tab.products.map(product => ({
      productId: product.productId,
      title: product.title,
      promotionalSticker: product.promotionalSticker || null,
      groups: product.groups.map(group => ({
        title: group.title,
        exponent: group.exponent,
        isMoreChannels: group.isMoreChannels,
        displayChevron: group.display_chevron,
        hasDescriptionsIcon: group.descriptions.some(
          description => description.icon?.link
        ),
        descriptions: group.descriptions.map(description => ({
          icon: getLinkHref(
            (description.icon?.link as Link | undefined) ?? null
          ),
          content: description.content
        })),
        channels: group.channels.map(channel => ({
          label: channel.channel.media?.description || null,
          href: getLinkHref(channel.channel.media?.link as Link)
        }))
      })),
      primaryButton:
        product.primaryButton?.label && product.primaryButton.link
          ? {
              label: product.primaryButton.label,
              link: getLinkHrefWithAuthentication(
                product.primaryButton.link as Link
              )
            }
          : null,
      secondaryButton:
        product.secondaryButton?.label && product.secondaryButton.link
          ? {
              label: product.secondaryButton.label,
              link: getLinkHrefWithAuthentication(
                product.secondaryButton.link as Link
              )
            }
          : null,
      moreChannelsIcon: {
        label: product.moreChannelsIcon?.description,
        href: product.moreChannelsIcon?.link.href
      },
      offerDetails: {
        availableCommitmentDurations: product.availableCommitmentDurations,
        commitmentConditions: product.commitmentConditions,
        engagementDuration: product.engagementDuration,
        engagementUnit: EngagementUnit[product.engagementUnit],
        netPrice: product.netPrice,
        price: product.price,
        priceInformation: product.priceInformation,
        promoDuration: product.promoDuration
      },
      teaserText: product.teaser_text
    }))
  });

  return {
    title: content.title,
    subtitle: content.subtitle,
    displayTabs: content.display_tabs,
    tabs: content.tabs.map(tab =>
      tab.template === 'banner' ? formatBanner(tab) : formatCard(tab)
    )
  };
};
